// v2 - cache bust
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { StudentDataProvider } from "@/contexts/StudentDataContext";
import StudentPageTransition from "@/components/StudentPageTransition";
import ProfessorLayout from "@/components/ProfessorLayout";
import OwnerLayout from "@/components/OwnerLayout";
import Login from "./pages/Login";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const StudentProfile = lazy(() => import("./pages/StudentProfile"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const OwnerAdmin = lazy(() => import("./pages/OwnerAdmin"));
const AnamneseForm = lazy(() => import("./pages/AnamneseForm"));
const AdminAnamneses = lazy(() => import("./pages/AdminAnamneses"));
const AdminAnamneseDetail = lazy(() => import("./pages/AdminAnamneseDetail"));
const StudentApp = lazy(() => import("./pages/StudentApp"));
const StudentLogin = lazy(() => import("./pages/StudentLogin"));
const StudentOnboarding = lazy(() => import("./pages/StudentOnboarding"));
const StudentResetPassword = lazy(() => import("./pages/StudentResetPassword"));
const StudentWorkout = lazy(() => import("./pages/StudentWorkout"));
const StudentWorkouts = lazy(() => import("./pages/StudentWorkouts"));
const StudentHistory = lazy(() => import("./pages/StudentHistory"));
const StudentChat = lazy(() => import("./pages/StudentChat"));
const StudentMessages = lazy(() => import("./pages/StudentMessages"));
const StudentInstall = lazy(() => import("./pages/StudentInstall"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ExerciseLibrary = lazy(() => import("./pages/ExerciseLibrary"));
const StudentMyProfile = lazy(() => import("./pages/StudentMyProfile"));
const AIUsageDashboard = lazy(() => import("./pages/AIUsageDashboard"));
const ProfessorSettings = lazy(() => import("./pages/ProfessorSettings"));
const OwnerPlans = lazy(() => import("./pages/OwnerPlans"));
const OwnerLogs = lazy(() => import("./pages/OwnerLogs"));
const OwnerConfig = lazy(() => import("./pages/OwnerConfig"));
const OwnerStaffManagement = lazy(() => import("./pages/OwnerStaffManagement"));


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
            <AnimatedRoutes />
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

function StudentRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <StudentPageTransition transitionKey={location.pathname}>
        <Routes location={location}>
          <Route index element={<StudentApp />} />
          <Route path="login" element={<StudentLogin />} />
          <Route path="onboarding" element={<StudentOnboarding />} />
          <Route path="redefinir-senha" element={<StudentResetPassword />} />
          <Route path="treinos" element={<StudentWorkouts />} />
          <Route path="treino/:workoutId" element={<StudentWorkout />} />
          <Route path="historico" element={<StudentHistory />} />
          <Route path="chat" element={<StudentChat />} />
          <Route path="mensagens" element={<StudentMessages />} />
          <Route path="instalar" element={<StudentInstall />} />
          <Route path="perfil" element={<StudentMyProfile />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </StudentPageTransition>
    </AnimatePresence>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const isStudentRoute = location.pathname.startsWith("/aluno");
  const routeKey = isStudentRoute ? "/aluno" : location.pathname;
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={routeKey}>
        <Route path="/login" element={<Login />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/anamnese" element={<AnamneseForm />} />
        <Route path="/aluno/*" element={
          <StudentDataProvider>
            <StudentRoutes />
          </StudentDataProvider>
        } />
        {/* Professor routes with branded layout */}
        <Route element={<ProtectedRoute><ProfessorLayout /></ProtectedRoute>}>
          <Route path="/" element={<Index />} />
          <Route path="/student/:id" element={<StudentProfile />} />
          <Route path="/admin/anamneses" element={<AdminAnamneses />} />
          <Route path="/admin/anamneses/:id" element={<AdminAnamneseDetail />} />
          <Route path="/exercicios" element={<ExerciseLibrary />} />
          <Route path="/configuracoes" element={<ProfessorSettings />} />
        </Route>
        {/* Owner routes with sidebar layout */}
        <Route element={<ProtectedRoute><OwnerLayout /></ProtectedRoute>}>
          <Route path="/admin" element={<OwnerAdmin />} />
          <Route path="/admin/planos" element={<OwnerPlans />} />
          <Route path="/admin/acessos" element={<OwnerStaffManagement />} />
          <Route path="/admin/ai-usage" element={<AIUsageDashboard />} />
          <Route path="/admin/logs" element={<OwnerLogs />} />
          <Route path="/admin/config" element={<OwnerConfig />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}

export default App;
